// COMPOSITION des deux axes de statut en UN SEUL badge lisible.
//
// ─────────────────────────────────────────────────────────────────────────────
// LE PROBLEME QUE CE MODULE RESOUT (constate sur capture, 2026-09-24).
//
// Le serveur produit deux axes DELIBEREMENT separes :
//
//   `displayStatus`  (tradovateDisplayStatus.ts)  → « mes trades remontent-ils ? »
//   `realtimeStatus` (tradovateRealtimeStatus.ts) → « a quelle VITESSE ? »
//
// Cette separation est JUSTE cote serveur, et l'en-tete de
// `tradovateRealtimeStatus.ts` explique pourquoi on ne la supprimera pas : un
// socket coupe laisse une connexion parfaitement synchronisee par le REST. Les
// fusionner cote MODELE obligerait a choisir entre deux mensonges.
//
// Mais l'UI les rendait comme DEUX BADGES DE MEME POIDS, cote a cote. Le
// lecteur y voit deux verdicts concurrents sur la meme chose :
//
//   [Synchronise]  [Connexion temps reel interrompue]   ← se contredisent ?
//   [Synchronise]  [Temps reel]                         ← redondants
//
// Deux axes independants dans le MODELE ne donnent pas deux badges dans l'UI.
// Ici, le second axe QUALIFIE le premier : il ne s'y oppose pas, il l'adverbe.
// « Synchronise » + « en direct » est une seule phrase, pas deux verdicts.
//
// D'ou ce module : un badge unique, dont le libelle porte le verbe ET l'adverbe.
// ─────────────────────────────────────────────────────────────────────────────
//
// ─────────────────────────────────────────────────────────────────────────────
// POURQUOI `reconnecting` EST INVISIBLE (decision produit, 2026-09-24).
//
// C'est le choix le plus contre-intuitif de ce module : on MASQUE une
// degradation reelle. La justification tient en deux faits.
//
// 1. ELLE EST NORMALE. Le jeton OAuth expire toutes les ~80 minutes ; le socket
//    meurt et se retablit en quelques secondes. Afficher un badge ambre a
//    chaque cycle produirait plusieurs alertes par jour sur un non-evenement --
//    et une alerte qu'on apprend a ignorer ne sert plus a rien le jour ou elle
//    est vraie. C'est exactement le raisonnement qui fixe
//    REALTIME_EVENT_INTERRUPTED_MS a une heure, applique a l'affichage.
//
// 2. ELLE EST NON ACTIONNABLE, ET BORNEE. `computeRealtimeStatus` ne laisse pas
//    une connexion dans `reconnecting` indefiniment : au-dela de
//    REALTIME_EVENT_INTERRUPTED_MS elle bascule en `interrupted`, qui lui EST
//    visible et actionnable. Masquer `reconnecting` ne masque donc jamais une
//    panne durable -- elle tombe d'elle-meme dans un etat qu'on affiche.
//
// ⚠️ CE DEUXIEME POINT EST LA CONDITION DE VALIDITE DU PREMIER. Si un jour
// `computeRealtimeStatus` pouvait retenir `reconnecting` sans echeance, cette
// regle deviendrait un masquage de panne. Un test structurel verrouille la
// chaine (cf. tradovateSyncPresentation.test.ts).
// ─────────────────────────────────────────────────────────────────────────────
//
// MODULE PUR : aucun import `wasp/*` ni React, pour que la regle soit testable
// sans monter le composant (meme convention que `tradovateSyncActions.ts`).

/** Intention visuelle du badge. Le composant mappe vers les tokens de theme. */
export type SyncTone =
  /** Tout va bien. Teal (primary) ou vert PnL pour le direct. */
  | "positive"
  /** Degradation reelle mais non bloquante. Ambre (warning). */
  | "warning"
  /** Panne : plus rien ne remonte. Rouge (destructive). */
  | "critical"
  /** Etat a constater, ni bon ni mauvais. Gris (muted). */
  | "neutral";

/** Icone a rendre. Le composant tient la correspondance vers lucide-react. */
export type SyncIcon = "radio" | "wifi" | "clock" | "pause" | "alert";

export interface SyncPresentationInput {
  /** Statut de sync (`displayStatus`, replie sur `status`). */
  displayStatus: string | undefined;
  /** Etat du flux temps reel (cf. `RealtimeStatus`). */
  realtimeStatus: string | undefined;
}

export interface SyncPresentation {
  /**
   * Cle i18n du libelle, sous `t.tradovate`. On renvoie une CLE et non un
   * texte : le module reste pur et testable sans charger les traductions.
   */
  labelKey: string;
  /** Cle i18n de l'aide au survol, ou `null` si le libelle se suffit. */
  helpKey: string | null;
  tone: SyncTone;
  icon: SyncIcon;
  /**
   * Faut-il afficher la ligne de fraicheur (« Derniere synchro il y a X ») ?
   *
   * Faux sur les etats ou la date induirait en erreur : sur `idle`,
   * `needs_reauth` ou `error`, la derniere synchro reussie est ancienne et
   * l'afficher suggererait que quelque chose tourne encore.
   */
  showFreshness: boolean;
  /**
   * Le flux est-il en DIRECT ? La ligne de fraicheur dit alors « a l'instant »
   * plutot qu'un age : sur un socket vivant, `lastSyncAt` date du dernier cycle
   * REST (jusqu'a 1 h en mode espace, cf. etape 3A) et sous-estimerait
   * gravement la fraicheur reelle des donnees.
   */
  isLive: boolean;
}

/** Statuts de sync qui signalent que la collecte fonctionne. */
const HEALTHY = new Set(["active", "syncing"]);

/**
 * Resout l'apparence du badge unique.
 *
 * ORDRE DE PRECEDENCE, et il compte : le statut de SYNC tranche en premier.
 * Un compte en erreur, en veille ou a reconnecter n'a pas de « vitesse » a
 * qualifier -- annoncer « en direct » sur une connexion qui ne collecte plus
 * serait le pire des affichages. Le temps reel ne raffine QUE les etats sains.
 */
export function resolveSyncPresentation(input: SyncPresentationInput): SyncPresentation {
  const status = input.displayStatus ?? "active";
  const realtime = input.realtimeStatus ?? "not_enabled";

  // ── Etats de sync qui priment sur toute notion de vitesse ────────────────

  if (status === "error") {
    return {
      labelKey: "statusError",
      helpKey: null,
      tone: "critical",
      icon: "alert",
      showFreshness: false,
      isLive: false,
    };
  }

  if (status === "needs_reauth") {
    return {
      labelKey: "statusNeedsReauth",
      helpKey: null,
      tone: "warning",
      icon: "alert",
      showFreshness: false,
      isLive: false,
    };
  }

  // Jeton autorise avec les identifiants d'un AUTRE utilisateur Tradovate :
  // aucun compte suivi dans la reponse du broker, cycle ignore a chaque tick.
  // Ambre et actionnable (Reconnecter), jamais « Synchronise » : 5 connexions
  // en prod l'affichaient, l'une depuis 24 jours, sans qu'un trade remonte.
  if (status === "foreign_token") {
    return {
      labelKey: "statusForeignToken",
      helpKey: "statusForeignTokenHint",
      tone: "warning",
      icon: "alert",
      showFreshness: false,
      isLive: false,
    };
  }

  if (status === "idle") {
    return {
      labelKey: "statusIdle",
      helpKey: "statusIdleHint",
      tone: "neutral",
      icon: "pause",
      showFreshness: false,
      isLive: false,
    };
  }

  if (status === "delayed") {
    return {
      labelKey: "statusDelayed",
      helpKey: null,
      tone: "warning",
      icon: "clock",
      showFreshness: true,
      isLive: false,
    };
  }

  // NEUTRE, ET PAS TEAL. « Aucun compte actif » n'est pas un succes : la
  // connexion vit mais ne rapporte rien. Le peindre en teal comme une sync
  // reussie invitait a ne pas y toucher, alors que l'utilisateur a
  // probablement un compte a importer ou a reactiver.
  if (status === "active_no_accounts") {
    return {
      labelKey: "statusActiveNoAccounts",
      helpKey: "statusActiveNoAccountsHint",
      tone: "neutral",
      icon: "wifi",
      showFreshness: true,
      isLive: false,
    };
  }

  // ── Etats sains : le temps reel qualifie la synchro ──────────────────────

  // NEUTRE, SANS ACTION. Connexion neuve ou reconnectee : le socket s'ouvre a
  // la fin de la premiere synchro. On previent (le direct arrive), on ne
  // propose rien : « Reconnecter » relancerait la sync initiale, donc
  // repousserait l'ouverture. Borne a une heure par `computeRealtimeStatus`.
  if (HEALTHY.has(status) && realtime === "starting") {
    return {
      labelKey: "statusSyncedStarting",
      helpKey: "statusSyncedStartingHelp",
      tone: "neutral",
      icon: "clock",
      showFreshness: true,
      isLive: false,
    };
  }

  if (HEALTHY.has(status) && realtime === "interrupted") {
    // AMBRE ET NON ROUGE, deliberement. Rien n'est casse : le cron REST prend
    // le relais toutes les 15 min. « Synchronise avec delai » est
    // litteralement vrai, la ou un badge rouge ferait croire a une panne et
    // generait le ticket que cet ecran est cense eviter.
    return {
      labelKey: "statusSyncedDelayed",
      helpKey: "statusSyncedDelayedHelp",
      tone: "warning",
      icon: "clock",
      showFreshness: true,
      isLive: false,
    };
  }

  if (HEALTHY.has(status) && realtime === "live") {
    return {
      labelKey: "statusSyncedLive",
      helpKey: null,
      tone: "positive",
      icon: "radio",
      showFreshness: true,
      isLive: true,
    };
  }

  // Reste : `not_enabled` (la grande majorite du parc, le socket n'ecrit pas
  // pour eux) et `reconnecting`, volontairement INVISIBLE (cf. l'en-tete).
  // Les deux affichent le meme badge sobre : la synchro fonctionne, et sa
  // vitesse n'est pas une information que l'utilisateur doit porter.
  return {
    labelKey: "statusActive",
    helpKey: null,
    tone: "positive",
    icon: "wifi",
    showFreshness: true,
    isLive: false,
  };
}
